import { memo, useEffect } from "react";
import { Helmet } from "react-helmet";
import { EMAIL_1, PHONE_1 } from "constants/configConstants";

import styles from "./NotFound.module.scss";
import Image from "@Components/Image";
import Header from "@Components/Header";
import loadable from "@loadable/component";
import useIsDesktop from "hooks/useIsDesktop";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { isApp, useIsNewNativeApp } from "hooks/useIsApp";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import getCookie from "helpers/getCookie";
import CustomLink from "@Components/CustomLink";
import useCustomWindowLocation from "@Components/CustomWindowLocation";

const HeaderDesktop = loadable(() => import("@Components/Header/HeaderDesktop"));
const FooterV2 = loadable(() => import("@Components/FooterV2/FooterV2"));
const FooterDesktop = loadable(() => import("@Components/FooterV2/FooterDesktop"));

const NotFound = (props: any) => {
  const { staticContext, isStatus500 } = props;
  const isDesktop = useIsDesktop();
  const isMiniProgram = useIsMiniProgram();
  // The new native app supplies its own chrome, so the site header (and its hamburger) and
  // footer must not render inside it — same rule the Terms and Privacy pages follow.
  //
  // The hook, not the non-hook `isNewNativeApp()`: this is also a real server-rendered 404
  // route, and the non-hook reader is `__CLIENT__`-gated. That would emit the header during SSR
  // and strip it on hydration — a mismatch plus a visible flash. `useIsNewNativeApp` reads the
  // shell Redux already holds from `appShellFromRequest` (`appReducer.ts:999`), so the server
  // and client agree.
  const isNativeShell = useIsNewNativeApp();
  const customWindowLocation = useCustomWindowLocation();

  const handleBackToHome = () => {
    if (isMiniProgram) {
      const userAuthToken = getCookie("user_auth_token") || "";
      // @ts-ignore
      if (window.wx && window.wx.miniProgram) {
        try {
          // Navigate to the mini program home page
          // @ts-ignore
          window.wx.miniProgram.redirectTo({
            url: `/pages/Home/Home?token=${encodeURIComponent(userAuthToken)}`,
          });
        } catch (error) {
          console.error("Failed to navigate to mini program:", error);
          // Fallback to regular navigation
          customWindowLocation({ url: "/" });
        }
      }
    } else {
      customWindowLocation({ url: "/" });
    }
  };

  // We have to check if staticContext exists
  // because it will be undefined if rendered through a BrowserRoute
  /* istanbul ignore next */
  if (staticContext) staticContext.statusCode = isStatus500 ? 500 : 404;

  useEffect(() => {
    if (isApp() && typeof window !== "undefined" && (window as any).ReactNativeWebView) {
      sendWebViewEvent(WEB_VIEW_APP_EVENTS.WEB_CRASH, {
        message: `Status code: ${isStatus500 ? 500 : 404} - ${window.location.href}`,
        stack: "N/A",
        componentStack: "N/A",
        componentName: "NotFoundPage",
      });
    }
  }, []);

  // RN app only: it renders its own native crash screen off the `WEB_CRASH` bridge event, so
  // web error UI underneath would double up. The new native app no longer listens to that event,
  // so this page must render there — otherwise the user is left on a blank WebView.
  if (isApp() && !isNativeShell) {
    return <></>;
  }

  return (
    <div className={formatClassNames(styles["page404-container"], isDesktop && styles.desktop)}>
      <Helmet title="Page not found (404) - amberstudent.com" />
      {!isNativeShell &&
        (isDesktop ? <HeaderDesktop /> : <Header addShadow showHamburger={!isMiniProgram} />)}
      <div className={styles["container"]}>
        {/* Right container */}
        <Image
          className={styles["section-404-right"]}
          src="https://prod-static-assets.amberstudent.com/images/404.svg"
          alt="404 page not found"
          width={360}
          height={360}
          isNotLazy
        />
        <div className={styles["section-404-container"]}>
          {/* Left Container */}
          <div className={styles["section-404-left"]}>
            <p className={styles["text-404"]}>{isStatus500 ? "500" : "404"}</p>
            <p className={styles["text-pnf"]}>
              {isStatus500 ? "Something went wrong." : "Page Not Found"}
            </p>
            <p>
              {isStatus500
                ? "Please try again in some time."
                : "The page you are looking for does not exist!"}
            </p>
            <p className={styles["text-contact-details"]}>
              We suggest you go back to the Home page or contact us at&nbsp;
              <CustomLink href={`mailto:${EMAIL_1}`}>
                <b>{EMAIL_1}</b>
              </CustomLink>
              or call &nbsp;
              <CustomLink href={`tel:${PHONE_1.full_number}`}>
                <b>
                  <span className={styles["country-code"]}>{PHONE_1.country_code}</span>{" "}
                  {PHONE_1.number}
                </b>
              </CustomLink>
              &nbsp;for help.
            </p>

            {/* Button */}
            {isMiniProgram ? (
              <button className={styles["button"]} onClick={handleBackToHome} type="button">
                &nbsp; Back To Home &nbsp; <i className="icon-arrow-right" />
              </button>
            ) : (
              <CustomLink className={styles["button"]} href="/">
                &nbsp; Back To Home &nbsp; <i className="icon-arrow-right" />
              </CustomLink>
            )}
          </div>
        </div>
      </div>
      {!isNativeShell && (isDesktop ? <FooterDesktop /> : <FooterV2 />)}
    </div>
  );
};

export default memo(NotFound);
